/**
 * Section-bounds helpers.
 * Centralizes the "navbar pins to top, footer pins to bottom, hero pins below navbar"
 * invariant so move/drag logic in SectionList, ComponentRenderer, RightSidebar, and
 * SectionItem stays in lockstep when a new pinned type is added.
 */

import type { PageComponent } from "../_types";

/** Section types that cannot be reordered by drag/move (always anchored to top/bottom). */
const PINNED_SECTION_TYPES = new Set<PageComponent["type"]>(["navbar", "hero", "footer"]);

/**
 * Whether a section is pinned (cannot be moved by drag-and-drop or up/down buttons).
 * @param section - The section component to check
 * @returns True for navbar, hero, and footer
 */
export function isPinnedSection(section: PageComponent | undefined | null): boolean {
  return !!section && PINNED_SECTION_TYPES.has(section.type);
}

/**
 * Whether the section list currently has a footer pinned at the bottom.
 * @param sections - The full ordered list of sections
 * @returns True when the last section is a footer
 */
export function hasFooterAtEnd(sections: PageComponent[]): boolean {
  return sections[sections.length - 1]?.type === "footer";
}

/**
 * Index of the last section that can be reordered into. When a footer exists at the
 * end, it's one slot earlier (so dragging stops before the footer).
 * @param sections - The full ordered list of sections
 * @returns Index of the last reorderable position; -1 if none
 */
export function lastReorderableIndex(sections: PageComponent[]): number {
  return hasFooterAtEnd(sections) ? sections.length - 2 : sections.length - 1;
}

/**
 * The index a non-footer, non-hero component should land at when appended "at the end" of the
 * page — i.e. right before an existing footer, or at the very end if there isn't one yet.
 * Shared by every place that computes this exact position (`insertComponentIntoArray`'s "end"
 * clamp, `applyQueuedChange`'s `insertComponent` tool, and `revertToActionSnapshot`'s
 * restore-a-deleted-component path) so the footer-anchoring rule has one owner.
 * @param components - The full component array (not filtered — navbar/hero included).
 * @returns The index to insert at.
 */
export function footerAnchoredEndIndex(components: PageComponent[]): number {
  const footerIndex = components.findIndex((c) => c.type === "footer");
  return footerIndex === -1 ? components.length : footerIndex;
}
